import { DEFAULT_LANGUAGE } from '@avanta/shared/constants/common';
import { AppointmentService } from '@avanta/shared/services/appointments';
import { type AvailableDates } from '@avanta/shared/types/appointments';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { QUERY_KEYS } from 'constants/common';
import { AppointmentsReservePayload } from 'pages/AppointmentsReserve/constants';
import { type AppointmentsReserveFormData } from 'pages/AppointmentsReserve/types';
import { mergeAvailableDates } from '../utils';

export interface UseFetchFreeHoursResult {
  availableDates: AvailableDates;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
}

export const useFetchFreeHours = (enabled = true): UseFetchFreeHoursResult => {
  const { control } = useFormContext<AppointmentsReserveFormData>();
  const [centerId, specialityId, contractId, patient, treatmentId] = useWatch({
    control,
    name: [
      AppointmentsReservePayload.backofficeCenterId,
      AppointmentsReservePayload.specialityBooked,
      AppointmentsReservePayload.contractId,
      AppointmentsReservePayload.patient,
      AppointmentsReservePayload.psychologistServiceBooked,
    ],
  });
  const patientId = patient?.id ?? '';
  const isCalendarQueryEnabled =
    enabled && !!specialityId && !!centerId && !!patientId && !!contractId && !!treatmentId;

  const {
    data: freeHoursData,
    isLoading,
    isFetching,
    isError,
  } = useQuery({
    queryKey: [
      QUERY_KEYS.FETCH_FREE_HOURS_AVAILABILITY,
      patientId,
      specialityId,
      centerId,
      contractId,
      treatmentId,
      DEFAULT_LANGUAGE,
    ],
    queryFn: () =>
      AppointmentService.getFreeHoursAvailability({
        patientId,
        specialityId: Number(specialityId),
        centerId,
        spokenLanguage: DEFAULT_LANGUAGE,
        contractId: Number(contractId),
        treatmentId,
      }),
    select: response => response.data,
    enabled: isCalendarQueryEnabled,
  });

  const availableDates = useMemo(
    () =>
      isCalendarQueryEnabled && freeHoursData ? mergeAvailableDates(freeHoursData.dailySlots) : {},
    [freeHoursData, isCalendarQueryEnabled]
  );

  return { availableDates, isLoading, isFetching, isError };
};
